import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCode,
  faFile,
  faFileCode,
  faFileCsv,
  faFileExcel,
  faFileImage,
  faFileLines,
  faFilePdf,
  faFileWord,
  faFileZipper,
} from '@fortawesome/pro-solid-svg-icons';

/** Presentation metadata for a generated file, keyed by extension.
 *
 *  Shared by `FileArtifactCard` (which uses `category` as its eyebrow label)
 *  and `ArtifactRow` (which uses `color` to tint its icon tile). Both used to
 *  carry their own near-identical copy of this table. */
export interface ArtifactFileMeta {
  readonly icon: IconDefinition;
  readonly typeName: string;
  /** Eyebrow label on the standalone card. */
  readonly category: string;
  /** Mantine palette family for the icon tile — never a raw hex. */
  readonly color: string;
}

export const SPREADSHEET_CATEGORY = 'GENERATED SPREADSHEET';
export const DOCUMENT_CATEGORY = 'GENERATED DOCUMENT';
export const GENERIC_CATEGORY = 'GENERATED FILE';

const SHEET = { category: SPREADSHEET_CATEGORY, color: 'green' } as const;
const DOC = { category: DOCUMENT_CATEGORY, color: 'blue' } as const;

export const ARTIFACT_FILE_META: Record<string, ArtifactFileMeta> = {
  xls: { icon: faFileExcel, typeName: 'Excel', ...SHEET },
  xlsx: { icon: faFileExcel, typeName: 'Excel', ...SHEET },
  xlsm: { icon: faFileExcel, typeName: 'Excel', ...SHEET },
  xlsb: { icon: faFileExcel, typeName: 'Excel', ...SHEET },
  csv: { icon: faFileCsv, typeName: 'CSV', ...SHEET },
  tsv: { icon: faFileCsv, typeName: 'TSV', ...SHEET },
  pdf: {
    icon: faFilePdf,
    typeName: 'PDF',
    category: DOCUMENT_CATEGORY,
    color: 'red',
  },
  doc: { icon: faFileWord, typeName: 'Word', ...DOC },
  docx: { icon: faFileWord, typeName: 'Word', ...DOC },
  txt: {
    icon: faFileLines,
    typeName: 'Text',
    category: DOCUMENT_CATEGORY,
    color: 'gray',
  },
  md: {
    icon: faFileCode,
    typeName: 'Markdown',
    category: DOCUMENT_CATEGORY,
    color: 'gray',
  },
  html: {
    icon: faCode,
    typeName: 'HTML',
    category: DOCUMENT_CATEGORY,
    color: 'orange',
  },
  json: {
    icon: faFileCode,
    typeName: 'JSON',
    category: GENERIC_CATEGORY,
    color: 'yellow',
  },
  zip: {
    icon: faFileZipper,
    typeName: 'Archive',
    category: GENERIC_CATEGORY,
    color: 'violet',
  },
  png: {
    icon: faFileImage,
    typeName: 'Image',
    category: GENERIC_CATEGORY,
    color: 'teal',
  },
  jpg: {
    icon: faFileImage,
    typeName: 'Image',
    category: GENERIC_CATEGORY,
    color: 'teal',
  },
  jpeg: {
    icon: faFileImage,
    typeName: 'Image',
    category: GENERIC_CATEGORY,
    color: 'teal',
  },
  gif: {
    icon: faFileImage,
    typeName: 'Image',
    category: GENERIC_CATEGORY,
    color: 'teal',
  },
  webp: {
    icon: faFileImage,
    typeName: 'Image',
    category: GENERIC_CATEGORY,
    color: 'teal',
  },
};

export const DEFAULT_ARTIFACT_FILE_META: ArtifactFileMeta = {
  icon: faFile,
  typeName: 'File',
  category: GENERIC_CATEGORY,
  color: 'gray',
};

export function artifactExtension(filename: string): string {
  const name = (filename || '').trim().toLowerCase();
  const lastDot = name.lastIndexOf('.');
  return lastDot === -1 ? '' : name.slice(lastDot + 1);
}

export function getArtifactFileMeta(filename: string): ArtifactFileMeta {
  return (
    ARTIFACT_FILE_META[artifactExtension(filename)] ??
    DEFAULT_ARTIFACT_FILE_META
  );
}
